'use client';
import React, { useState } from 'react';

interface Plan {
  id: string;
  name: string;
  tagline: string;
  price: string;
  period: string;
  badge?: string;
  badgeColor?: string;
  gradient: string;
  features: string[];
  highlight?: boolean;
  passPrice?: string;
}

const basicPlans: Plan[] = [
  {
    id: 'consonantal-core',
    name: 'Consonantal Core',
    tagline: 'Sports & more for an unbeatable price',
    price: '$19.99',
    period: '/mo',
    gradient: 'plan-card-orange',
    features: ['30+ channels', 'ESPN, ESPN2', 'Sports Extra available', '1 stream at a time', 'Cloud DVR included'],
    passPrice: '$4.99/day',
  },
  {
    id: 'consonantal-select',
    name: 'Consonantal Select',
    tagline: 'News & entertainment for an unbeatable price',
    price: '$19.99',
    period: '/mo',
    gradient: 'plan-card-blue',
    features: ['40+ channels', 'CNN, FOX News, MSNBC', 'News Extra available', '3 streams at a time', 'Cloud DVR included'],
    passPrice: '$4.99/day',
  },
  {
    id: 'consonantal-bundle',
    name: 'Consonantal Bundle',
    tagline: 'Score sports & news for less',
    price: '$34.99',
    period: '/mo',
    badge: 'BEST VALUE',
    badgeColor: '#ffa300',
    gradient: 'plan-card-combo',
    features: ['50+ channels', 'Sports + News combined', 'Up to 4 streams', 'Cloud DVR included', 'FOX One app access'],
    highlight: true,
  },
];

const fullPlans: Plan[] = [
  {
    id: 'sling-orange',
    name: 'SCD Orange',
    tagline: 'Best for sports & family',
    price: '$45.99',
    period: '/mo',
    gradient: 'plan-card-orange',
    features: ['35+ channels', 'ESPN, ESPN2, ESPN3', 'Disney Channel', '1 stream at a time', '50hr Cloud DVR'],
    passPrice: '$4.99/day',
  },
  {
    id: 'sling-blue',
    name: 'SCD Blue',
    tagline: 'Best for news & entertainment',
    price: '$45.99',
    period: '/mo',
    gradient: 'plan-card-blue',
    features: ['45+ channels', 'CNN, FOX News, MSNBC', 'NBC, FOX, ABC locals', '3 streams at a time', '50hr Cloud DVR'],
  },
  {
    id: 'sling-orange-blue',
    name: 'SCD Orange & Blue',
    tagline: 'Big selection, great value',
    price: '$60.99',
    period: '/mo',
    badge: 'MOST POPULAR',
    badgeColor: '#00b9ff',
    gradient: 'plan-card-combo',
    features: ['55+ channels', 'Sports + News + Entertainment', 'Up to 4 streams', '50hr Cloud DVR', 'FOX One app access'],
    highlight: true,
  },
];

function PlanCard({ plan }: { plan: Plan }) {
  return (
    <div className={`plan-card flex flex-col h-full relative overflow-hidden`}>
      {/* Gradient top bar */}
      <div className={`h-1.5 w-full ${
        plan.gradient === 'plan-card-orange' ? 'bg-gradient-orange' :
        plan.gradient === 'plan-card-blue'? 'bg-gradient-blue' : 'bg-gradient-orange-blue'
      }`} />
      
      {plan.badge && (
        <div
          className="absolute top-4 right-4 text-[10px] font-black px-2.5 py-1 rounded-full text-black"
          style={{ backgroundColor: plan.badgeColor }}
        >
          {plan.badge}
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        {/* Plan name */}
        <div className="mb-1">
          <div className={`text-xs font-black uppercase tracking-widest mb-1 ${
            plan.gradient === 'plan-card-orange' ? 'text-[#ffa300]' :
            plan.gradient === 'plan-card-blue' ? 'text-[#00b9ff]' :
            'text-gradient-glow'
          }`}>
            sling consonantal digital
          </div>
          <h3 className="text-xl font-black text-white">{plan.name}</h3>
        </div>

        <p className="text-[#979797] text-sm mb-4 leading-snug">{plan.tagline}</p>

        {/* Price */}
        <div className="mb-4">
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-black text-white">{plan.price}</span>
            <span className="text-[#979797] text-sm">{plan.period}</span>
          </div>
          {plan.passPrice && (
            <div className="text-[#979797] text-xs mt-1">
              or passes starting at <span className="text-[#ffa300] font-bold">{plan.passPrice}</span>
            </div>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-1">
          {plan.features.map((feature, i) => (
            <li key={i} className="flex items-center gap-2 text-sm text-[#c3c3c3]">
              <svg className="w-4 h-4 text-[#55cc35] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        {/* CTA */}
        <a
          href="#"
          className={`w-full text-center py-3 rounded-btn font-black text-sm transition-all duration-200 ${
            plan.highlight
              ? 'btn-primary' :'btn-outline'
          }`}
        >
          Add to Cart
        </a>
      </div>
    </div>
  );
}

export default function PlansSection() {
  const [activeTab, setActiveTab] = useState<'basic' | 'full'>('basic');

  return (
    <section id="plans" className="py-16 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="flex justify-center mb-10">
          <div className="bg-[#20222f] rounded-full p-1.5 flex gap-1 border border-white/10">
            <button
              className={`tab-btn ${activeTab === 'basic' ? 'active' : ''}`}
              onClick={() => setActiveTab('basic')}
            >
              Basic Plans
            </button>
            <button
              className={`tab-btn ${activeTab === 'full' ? 'active' : ''}`}
              onClick={() => setActiveTab('full')}
            >
              Full Plans
            </button>
          </div>
        </div>

        {/* Plan heading */}
        <div className="text-center mb-8">
          <h2 className="text-[28px] sm:text-[32px] font-black text-white">
            {activeTab === 'basic' ? 'Basic Plans' : 'Full Plans'}
          </h2>
          <p className="text-[#979797] mt-2">
            {activeTab === 'basic' ?'Affordable plans to get you started with live TV' :'Full channel lineups for the ultimate streaming experience'}
          </p>
        </div>

        {/* Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(activeTab === 'basic' ? basicPlans : fullPlans).map((plan) => (
            <PlanCard key={plan.id} plan={plan} />
          ))}
        </div>

        {/* Compare link */}
        <div className="text-center mt-8">
          <a href="#" className="text-[#00b9ff] font-bold text-sm hover:underline transition-all">
            Compare plans →
          </a>
        </div>
      </div>
    </section>
  );
}
